/**
 * Uma tabela do documento como o DOCX a desenhou (E202.F1, GUS-145): a largura de cada coluna, o
 * cabeçalho repetido, o fundo e a junção de cada célula e a tabela dentro da célula (a escala dos
 * gráficos). A abertura (a faixa navy) vem sem filetes.
 *
 * E202.F2 (ADR-0145 §7): no mapa do relatório de risco, as células do texto, do uso, da resposta e
 * da providência abrem o editor do campo (`linhaEditavel`), numa linha logo abaixo da do risco.
 *
 * E203.F2 (ADR-0146 §1): cada linha do corpo responde ao clique (e ao Enter) e abre o painel do
 * dado com a origem dela, ou a da tabela; a célula que abre o editor segue abrindo o editor.
 */
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import type {
  CelulaDoBlocoData,
  GradeDoBlocoData,
  LinhaDoBlocoData,
  OrigemDoBlocoData,
} from '@/lib/documentoDaSaida';

import { clicavel, REALCE_CLICAVEL, REALCE_DO_DADO, semSelecao } from './clicavel';
import { PALETA } from './paleta';
import { textoDaLinha, useAbrirDado } from './pedidoDoDado';
import { ParagrafosDoDocumento } from './TrechosDoDocumento';

/** A linha da tabela com campos editáveis: o trecho de cada coluna (`null`: protegida). */
export interface LinhaEditavel {
  trechos: readonly (string | null)[];
  abrir: (trechoId: string) => void;
  /** O que vem logo depois da linha, na largura da tabela (o editor do campo aberto). */
  depois: ReactNode;
}

function Celula({
  celula,
  cabecalho,
  filete,
  abrir,
}: {
  celula: CelulaDoBlocoData;
  cabecalho: boolean;
  filete: boolean;
  abrir?: (() => void) | null;
}): JSX.Element {
  const Tag = cabecalho ? 'th' : 'td';
  const editavel = abrir !== undefined && abrir !== null;
  return (
    <Tag
      colSpan={celula.colunas > 1 ? celula.colunas : undefined}
      className={`px-2 py-1 text-left align-top font-normal ${editavel ? REALCE_CLICAVEL : ''}`}
      {...clicavel(abrir ?? null)}
      data-editavel={editavel ? 'sim' : undefined}
      data-testid={editavel ? 'documento-celula-editavel' : undefined}
      style={{
        backgroundColor: celula.fundo !== null ? `#${celula.fundo}` : undefined,
        borderBottom: filete ? `1px solid ${PALETA.linha2}` : undefined,
      }}
    >
      <ParagrafosDoDocumento paragrafos={celula.paragrafos} naCelula />
      {celula.grades.map((grade, i) => (
        <GradeDoDocumento key={i} grade={grade} semFiletes />
      ))}
    </Tag>
  );
}

/** As propriedades da linha que abre o painel do dado: o clique fora da célula do editor e o Enter. */
function linhaDoDado(
  abrir: ((elemento: HTMLElement) => void) | null,
  origem: OrigemDoBlocoData | null,
) {
  if (abrir === null || origem === null) return {};
  return {
    tabIndex: 0,
    title: ptBR.painelDoDado.dica,
    className: REALCE_DO_DADO,
    'data-origem': `${origem.tipo}:${origem.chave}`,
    'data-testid': 'documento-linha-do-dado',
    onClick: (e: MouseEvent<HTMLTableRowElement>) => {
      const alvo = e.target instanceof Element ? e.target : null;
      if (alvo?.closest('[data-editavel]') || !semSelecao()) return;
      abrir(e.currentTarget);
    },
    onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
      if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
        e.preventDefault();
        abrir(e.currentTarget);
      }
    },
  };
}

function Linha({
  linha,
  filete,
  editavel = null,
  origemDaGrade = null,
  secao = null,
}: {
  linha: LinhaDoBlocoData;
  filete: boolean;
  editavel?: LinhaEditavel | null;
  origemDaGrade?: OrigemDoBlocoData | null;
  secao?: string | null;
}): JSX.Element {
  const abrirDado = useAbrirDado();
  const colunas = linha.celulas.reduce((n, c) => n + c.colunas, 0);
  const origem = linha.cabecalho ? null : (linha.origem ?? origemDaGrade);
  const abrir =
    abrirDado === null || origem === null
      ? null
      : (elemento: HTMLElement) =>
          abrirDado({ origem, impresso: textoDaLinha(linha), secao, elemento });
  return (
    <>
      <tr {...linhaDoDado(abrir, origem)}>
        {linha.celulas.map((celula, i) => {
          const trecho = editavel?.trechos[i] ?? null;
          const abrir = trecho !== null && editavel !== null ? () => editavel.abrir(trecho) : null;
          return (
            <Celula
              key={i}
              celula={celula}
              cabecalho={linha.cabecalho}
              filete={filete}
              abrir={abrir}
            />
          );
        })}
      </tr>
      {editavel?.depois ? (
        <tr>
          <td colSpan={colunas} className="px-1 py-1">
            {editavel.depois}
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function GradeDoDocumento({
  grade,
  semFiletes = false,
  linhaEditavel,
  origem = null,
  secao = null,
}: {
  grade: GradeDoBlocoData;
  semFiletes?: boolean;
  /** A linha `i` do corpo (sem o cabeçalho) com campos editáveis, ou `null`. */
  linhaEditavel?: ((i: number) => LinhaEditavel | null) | undefined;
  /** E203.F2: a origem da tabela, para a linha sem origem própria. */
  origem?: OrigemDoBlocoData | null;
  secao?: string | null;
}): JSX.Element {
  const cabecalho = grade.linhas.filter((l) => l.cabecalho);
  const corpo = grade.linhas.filter((l) => !l.cabecalho);
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed border-collapse" data-testid="documento-tabela">
        {grade.larguras.length > 0 ? (
          <colgroup>
            {grade.larguras.map((largura, i) => (
              <col key={i} style={{ width: `${(largura * 100).toFixed(2)}%` }} />
            ))}
          </colgroup>
        ) : null}
        {cabecalho.length > 0 ? (
          <thead>
            {cabecalho.map((linha, i) => (
              <Linha key={i} linha={linha} filete={false} />
            ))}
          </thead>
        ) : null}
        <tbody>
          {corpo.map((linha, i) => (
            <Linha
              key={i}
              linha={linha}
              filete={!semFiletes}
              editavel={linhaEditavel?.(i) ?? null}
              origemDaGrade={origem}
              secao={secao}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
